import { useEffect, useState } from "react";
import {
  APPROVALS,
  CATEGORIES,
  STATUSES,
  createMember,
  updateMember,
} from "../../services/adminService";

const EMPTY = {
  firstName: "",
  lastName: "",
  membershipNumber: "",
  email: "",
  phone: "",
  category: CATEGORIES[0],
  organization: "",
  approval: "Pending",
  status: "Inactive",
  certificate: "Not recorded",
};

export default function MemberFormModal({ member, onClose, onSaved }) {
  const editing = !!member;
  const [form, setForm] = useState(
    editing ? { ...EMPTY, ...member, membershipNumber: member.memberNumber } : EMPTY
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const statusOptions = Array.from(new Set([...STATUSES, form.status]));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.firstName.trim()) return setError("First name is required.");
    setSaving(true);
    setError("");
    // Write the surname back to the field the record already uses (secondName or lastName)
    const surnameField = editing ? member.surnameField : "lastName";
    const data = {
      firstName: form.firstName.trim(),
      [surnameField]: form.lastName.trim(),
      membershipNumber: form.membershipNumber.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      category: form.category,
      organization: form.organization.trim(),
      approval: form.approval,
      status: form.status,
      certificate: form.certificate,
    };
    const label = `${data.firstName} ${form.lastName.trim()}`.trim();
    try {
      if (editing) await updateMember(member.id, data, label);
      else await createMember(data, label);
      onSaved(editing ? `${label} updated` : `${label} added to the register`);
    } catch (err) {
      console.error(err);
      setError("Could not save. Check your connection and permissions.");
      setSaving(false);
    }
  };

  return (
    <div className="na-overlay" onMouseDown={onClose}>
      <form className="na-modal" onMouseDown={(e) => e.stopPropagation()} onSubmit={submit}>
        <h2>{editing ? "Edit member" : "Insert member"}</h2>

        <div className="na-grid">
          <label>
            First name *
            <input value={form.firstName} onChange={set("firstName")} autoFocus />
          </label>
          <label>
            Surname
            <input value={form.lastName} onChange={set("lastName")} />
          </label>
          <label>
            Membership number
            <input value={form.membershipNumber} onChange={set("membershipNumber")} placeholder="e.g. NSK-050" />
          </label>
          <label>
            Category
            <select value={form.category} onChange={set("category")}>
              {CATEGORIES.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </label>
          <label>
            Email
            <input type="email" value={form.email} onChange={set("email")} />
          </label>
          <label>
            Phone
            <input value={form.phone} onChange={set("phone")} />
          </label>
          <label className="na-span2">
            Organization
            <input value={form.organization} onChange={set("organization")} />
          </label>
          <label>
            Executive approval
            <select value={form.approval} onChange={set("approval")}>
              {APPROVALS.map((a) => (
                <option key={a}>{a}</option>
              ))}
            </select>
          </label>
          <label>
            Certificate
            <select value={form.certificate} onChange={set("certificate")}>
              <option>Not recorded</option>
              <option>Issued</option>
            </select>
          </label>
          <label className="na-span2">
            Status (only Active members appear in the public verification form)
            <select value={form.status} onChange={set("status")}>
              {statusOptions.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </label>
        </div>

        {error && <p className="na-error">{error}</p>}

        <div className="na-modal-actions">
          <button type="button" className="na-btn" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="na-btn na-btn-primary" disabled={saving}>
            {saving ? "Saving…" : editing ? "Save changes" : "Insert member"}
          </button>
        </div>
      </form>
    </div>
  );
}
